import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, PaperPlaneTilt, EnvelopeSimple, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

const RECIPIENT = "info@accuratemonkey.com";

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || message.trim().length < 5) {
      toast.error("Please fill in all fields. Message must be at least 5 characters.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", { name: name.trim(), email: email.trim(), message: message.trim() });
      setSent(true);
      setName(""); setEmail(""); setMessage("");
      toast.success("Message sent. We'll be in touch.");
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to send. Please try again.";
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-[1000px] mx-auto px-6 py-10" data-testid="contact-page">
      <Link to="/" data-testid="contact-back" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white mb-6">
        <ArrowLeft size={12} /> Back to picks
      </Link>

      <div className="mb-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#007AFF] mb-1">Inquiries</div>
        <h1 className="font-display text-5xl sm:text-6xl leading-none">GET IN TOUCH.</h1>
        <p className="text-sm text-zinc-400 mt-4 max-w-2xl leading-relaxed">
          Subscription questions, partnership pitches, press, or just want to ask Charlie and
          Dakota what they think of your team this week? Drop us a line — we read every message.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Direct email card */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2 bg-[#121212] border border-white/10 rounded-sm p-6 space-y-5"
          data-testid="contact-direct-email"
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#22C55E]">
            <ShieldCheck size={14} weight="fill" /> Direct line
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Email us anytime</div>
            <a
              href={`mailto:${RECIPIENT}`}
              data-testid="contact-mailto"
              className="font-display text-2xl text-white hover:text-[#007AFF] transition-colors break-all inline-flex items-center gap-2"
            >
              <EnvelopeSimple size={20} weight="fill" className="text-[#007AFF] flex-shrink-0" />
              {RECIPIENT}
            </a>
          </div>
          <div className="border-t border-white/5 pt-4 text-xs text-zinc-400 leading-relaxed">
            We typically reply within 24 hours. For urgent subscription or billing issues,
            include your account email so we can pull up your record faster.
          </div>
          <div className="border-t border-white/5 pt-4 text-[10px] uppercase tracking-widest text-zinc-500">
            21+ · Not financial advice · Gamble responsibly · 1-800-GAMBLER
          </div>
        </motion.div>

        {/* Contact form */}
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-3 bg-[#121212] border border-white/10 rounded-sm p-6 space-y-4"
          data-testid="contact-form"
        >
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#007AFF] mb-2">Send a message</div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5" htmlFor="contact-name">Name</label>
            <input
              id="contact-name"
              data-testid="contact-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={120}
              className="w-full bg-[#0A0A0A] border border-white/15 hover:border-white/30 focus:border-[#007AFF] outline-none rounded-sm px-3 py-2.5 text-sm font-mono-d transition-colors"
              placeholder="Your name"
            />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5" htmlFor="contact-email">Email</label>
            <input
              id="contact-email"
              data-testid="contact-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-[#0A0A0A] border border-white/15 hover:border-white/30 focus:border-[#007AFF] outline-none rounded-sm px-3 py-2.5 text-sm font-mono-d transition-colors"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5" htmlFor="contact-message">Message</label>
            <textarea
              id="contact-message"
              data-testid="contact-message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              rows={6}
              maxLength={5000}
              className="w-full bg-[#0A0A0A] border border-white/15 hover:border-white/30 focus:border-[#007AFF] outline-none rounded-sm px-3 py-2.5 text-sm font-mono-d transition-colors resize-y"
              placeholder="What's on your mind?"
            />
            <div className="text-[10px] text-zinc-600 mt-1 text-right font-mono-d">{message.length}/5000</div>
          </div>

          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit"
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#007AFF] hover:bg-[#0064D6] disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs uppercase tracking-[0.25em] font-bold rounded-sm transition-colors"
          >
            <PaperPlaneTilt size={14} weight="fill" />
            {sending ? "Sending…" : "Send message"}
          </button>

          {sent && (
            <div className="text-[11px] text-[#22C55E] text-center mt-2" data-testid="contact-sent">
              Message delivered. We&apos;ll reply to your email shortly.
            </div>
          )}
        </motion.form>
      </div>
    </div>
  );
}
